import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Warning } from "@phosphor-icons/react";

const EFFECTIVE = "2026-06-01";
const VERSION = "draft-v0.1";

const TERMS = [
  ["Virtual Gold — no cash value", "Aurumward uses virtual in-game currency (\"Gold\") only. Gold has no real-world monetary value, cannot be purchased, sold, redeemed, withdrawn, or exchanged for cash, cryptocurrency, or anything of value. There are currently no payments, real earnings, crypto, or sponsor rewards in Aurumward."],
  ["Age eligibility", "You must meet the minimum age required in your jurisdiction to use Aurumward. If you are under the required age, do not use the service."],
  ["Account rules", "You are responsible for your account and for keeping your credentials secure. One account per person. Do not share, sell, or transfer accounts."],
  ["User conduct", "No cheating, automation, exploitation of bugs, harassment, hate speech, or attempts to manipulate leaderboards, rivals, or other players. We may suspend or remove accounts that violate these rules."],
  ["Leaderboards & competition", "Rankings are for entertainment. We may recompute, correct, or remove scores, and exclude demo/test accounts, to preserve fairness. Standings confer no monetary prize."],
  ["Intellectual property", "Aurumward, its name, artwork, audio, and content are owned by their respective creators. You may not copy, redistribute, or create derivatives without permission."],
  ["Account deletion & retention", "You may request account deletion at any time from Settings. Deletion uses a 30-day grace period during which your account is deactivated and removed from public surfaces; you may cancel within that window. After 30 days your player data is permanently purged, retaining only legally necessary anonymized aggregate records."],
  ["PWA / offline behavior", "Aurumward may be installed as a Progressive Web App and cache assets for offline use. Cached content may be updated when you reconnect."],
  ["Audio features", "Optional decorative audio (soundtrack, crew motifs) may be offered. Audio never affects gameplay, scoring, or outcomes, and is off by default for ordinary players."],
  ["Jurisdiction", "[Jurisdiction placeholder — to be completed by legal counsel.]"],
];

const PRIVACY = [
  ["What we collect", "Account details you provide (email, username), gameplay records, and privacy-safe technical/analytics events. We do not sell your personal data."],
  ["Analytics & cookies", "We use minimal, aggregate, privacy-safe analytics and essential cookies/local storage for authentication and preferences. We do not log passwords, tokens, full IP addresses, or private content in application analytics."],
  ["Audio & preferences", "Your audio and gameplay preferences are stored to your account so they persist across devices."],
  ["Data retention & deletion", "You can request deletion from Settings. We apply a 30-day grace period, then permanently purge player-owned data (gameplay, wallet, crew, Prestige, rivals, notifications, cards, reports, analytics identifiers, audio settings, and generated artifacts), keeping only legally necessary anonymized aggregates."],
  ["No payments / no financial data", "Aurumward currently processes no payments and collects no payment or financial data. Virtual Gold has no cash value."],
  ["Security", "We use industry-standard measures to protect your data. No system is perfectly secure; please use a strong, unique password."],
  ["Children", "Aurumward is not directed to children below the minimum age for your jurisdiction."],
  ["Contact", "[Contact method placeholder — privacy@aurumward.example]"],
  ["Jurisdiction", "[Jurisdiction placeholder — to be completed by legal counsel.]"],
];

export default function Legal({ doc = "terms" }) {
  const isPrivacy = doc === "privacy";
  const title = isPrivacy ? "Privacy Policy" : "Terms of Service";
  const sections = isPrivacy ? PRIVACY : TERMS;
  return (
    <div className="mx-auto max-w-3xl px-4 py-10" data-testid={`legal-${doc}`}>
      <Link to="/" className="mb-6 inline-flex items-center gap-1 text-sm text-ff-brass hover:text-ff-gold" data-testid="legal-back">
        <ArrowLeft size={15} /> Back to Aurumward
      </Link>

      <div className="mb-6 flex items-start gap-2 rounded-md border border-amber-500/40 bg-amber-500/10 p-3" data-testid="legal-draft-banner">
        <Warning size={18} weight="fill" className="mt-0.5 text-amber-300" />
        <p className="text-sm text-amber-100">
          <b>Draft — Legal Review Required.</b> This document is a placeholder for internal preparation only and is not finalized legal advice.
        </p>
      </div>

      <h1 className="font-display text-4xl text-ff-gold">{title}</h1>
      <p className="mt-1 text-xs text-ff-muted">Effective: {EFFECTIVE} · Version: {VERSION} · Aurumward</p>

      <div className="mt-8 space-y-6">
        {sections.map(([h, body], i) => (
          <section key={i} data-testid={`legal-section-${i}`}>
            <h2 className="text-lg font-semibold text-ff-parchment">{h}</h2>
            <p className="mt-1 text-sm leading-relaxed text-ff-muted">{body}</p>
          </section>
        ))}
      </div>

      <div className="mt-10 flex gap-4 text-sm text-ff-brass">
        <Link to="/terms" className="hover:text-ff-gold" data-testid="legal-link-terms">Terms</Link>
        <Link to="/privacy" className="hover:text-ff-gold" data-testid="legal-link-privacy">Privacy</Link>
      </div>
    </div>
  );
}
